import { Div, Text } from "atomize"
import { useContext, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { theme } from "../settings/config"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import { canEditLesson, getSportIcon } from "../utils/func.utils"
import { formatMinutes, lessonEndMinutes, lessonStartMinutes } from "../utils/week.utils"
import WeekCourtPill from "./WeekCourtPill"
import { cornersOf } from "./ElsewhereLessonRow"
import SwipeActions from "../lesson/SwipeActions"
import LessonActions from "../lesson/LessonActions"

// "Gioco io" nell'elenco del giorno e nella settimana del telefono: una riga
// sola, come le lezioni negli altri circoli, perché non è una lezione e non
// ha niente da segnare — allievi, presenze, pagamenti. Dice quando, dove e
// con chi; toccandola si apre quanto basta per cambiarla. Di seguito a una
// lezione, o a un'altra volta che giochi, ci sta attaccata (`joins`).
// `swipeable` spento ferma i gesti: nel mezzo del passaggio giorno →
// settimana non si trascina niente.
function MatchRow({ lesson, joins, m, swipeable = true }) {
	const { coach } = useContext(profileContext)
	const { selectedSchool, getSelectedSchool } = useContext(schoolContext)
	const { getLessonType } = useContext(sharedDataContext)
	const [isOpen, setOpen] = useState(false)

	// "Gioco io" lo dice chi gioca: guardando le lezioni di Giulia, è Giulia
	const colleague = getSelectedSchool()?.coaches?.[lesson.coachID]?.coachName?.split(" ")[0]
	const name =
		lesson.coachID !== coach?.coachID && colleague
			? `Gioca ${colleague}`
			: (getLessonType(lesson.lessonType)?.lessonTypeName ?? "Gioco io")
	const time = `${formatMinutes(lessonStartMinutes(lesson))}–${formatMinutes(lessonEndMinutes(lesson))}`
	const notes = lesson.lessonNotes?.trim()
	const canEdit = canEditLesson(coach, selectedSchool, lesson)

	return (
		<Div m={m}>
			{/* gli stessi gesti delle lezioni: verso destra si duplica, verso
			    sinistra si modifica (vedi SwipeActions) */}
			<SwipeActions lessonData={lesson} canEdit={canEdit && swipeable}>
				{isDragging => (
					<Div
						bg={theme.colors.match}
						style={{
							// mentre la si trascina si stacca tutta
							borderRadius: cornersOf(isDragging ? {} : joins)
								.map(r => `${r}px`)
								.join(" "),
						}}
						data-testid="match-row">
						<Div
							tag="button"
							type="button"
							aria-expanded={isOpen}
							aria-label={`${time}, ${name}`}
							w="100%"
							d="flex"
							align="center"
							p="0.45rem 0.75rem"
							bg="transparent"
							border="0"
							cursor="pointer"
							textAlign="left"
							style={{ gap: "0.75rem", font: "inherit" }}
							onClick={() => setOpen(!isOpen)}>
							<Text
								textWeight="bold"
								textSize="caption"
								textColor={theme.colors.match_dark}
								style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
								{time}
							</Text>
							<Div flexGrow="1" style={{ minWidth: 0 }}>
								<Text textSize="body" textWeight="600" textColor={theme.colors.match_dark}>
									{name}
								</Text>
								{notes && (
									<Text
										textSize="tiny"
										textColor={theme.colors.match_dark}
										style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
										{notes}
									</Text>
								)}
							</Div>
							<WeekCourtPill lesson={lesson} compact />
							<Div
								w="28px"
								h="28px"
								rounded="circle"
								bg={theme.colors.match_fill}
								d="flex"
								align="center"
								justify="center"
								style={{ flexShrink: 0 }}>
								<FontAwesomeIcon
									icon={getSportIcon(lesson.sportID)}
									color={theme.colors.white}
									size="sm"
								/>
							</Div>
						</Div>

						<AnimatePresence initial={false}>
							{isOpen && canEdit && (
								<motion.div
									initial={{ height: 0, opacity: 0 }}
									animate={{ height: "auto", opacity: 1 }}
									exit={{ height: 0, opacity: 0 }}
									transition={{ ease: "easeInOut", duration: 0.2 }}
									style={{ overflow: "hidden" }}>
									{/* gli stessi bottoni delle lezioni */}
									<LessonActions
										lessonData={lesson}
										canEdit={canEdit}
										m={{ t: "0.25rem", x: "0.75rem", b: "0.75rem" }}
									/>
								</motion.div>
							)}
						</AnimatePresence>
					</Div>
				)}
			</SwipeActions>
		</Div>
	)
}

export default MatchRow
